import { readFile, validateFileType } from '@hashnode/file-parsing'
import matter from 'gray-matter'
import path from 'path'
import {
	basePageContentSchema,
	ParsedGuideWithVersionsConfig,
	ParsedPageContentConfig,
} from '../../schemas'
import { applyGuideAndVersionPages } from '../../walk'
import { generateSlug } from '../generate-slug'
import { findImagesInMdx, ImageInfo } from '../images'
import { replaceRelativeLocalPathsWithAbsoluteLocalPaths } from '../linking'

const defaultTitle = 'Untitled Page'

export function parsePageContent(filePath: string): ParsedPageContentConfig {
	const format = validateFileType(filePath)

	const pageContent = readFile(filePath)

	const { content, data, matter: rawFrontMatter } = matter(pageContent)

	// Check if there is no front matter by checking if data is empty and rawFrontMatter is empty
	const hasFrontMatter =
		Object.keys(data).length > 0 || (rawFrontMatter && rawFrontMatter.trim().length > 0)

	if (!hasFrontMatter) {
		throw new Error(`Missing front matter`)
	}

	const numberOfLines = hasFrontMatter ? (rawFrontMatter ?? '').split('\n').length + 1 : 0

	const title = data.title ?? defaultTitle
	const defaultVisibility = data.visibility ?? 'public'

	const slug = generateSlug({
		title,
		slug: data.slug ?? '',
	})

	const parsedContent = {
		...data,
		title,
		defaultVisibility,
		content: content ?? '',
		format,
		numberOfMatterLines: numberOfLines,
		slug,
	}

	return basePageContentSchema.parse(parsedContent)
}

export function replaceLocalRelativePageLinksWithAbsolutePaths({
	configs,
}: {
	configs: ParsedGuideWithVersionsConfig[]
}) {
	return applyGuideAndVersionPages(configs, (item) => {
		const { type } = item

		if (type === 'page') {
			const content = replaceRelativeLocalPathsWithAbsoluteLocalPaths({
				// Path should be relative to the current file
				folderPath: path.dirname(item.filePath),
				content: item.content ?? '',
			})
			return {
				...item,
				content,
			}
		}

		return item
	})
}

export function replaceLocalRelativeImageLinksWithAbsolutePaths({
	configs,
}: {
	configs: ParsedGuideWithVersionsConfig[]
}) {
	let imageAssets: ImageInfo[] = []

	const result = applyGuideAndVersionPages(configs, (item) => {
		const { type } = item

		if (type === 'page') {
			const { images, updatedContent: content } = findImagesInMdx({
				content: item.content || '',
				folderPath: path.dirname(item.filePath),
			})
			imageAssets = [...imageAssets, ...images]
			return {
				...item,
				content,
			}
		}

		return item
	})

	return {
		configs: result,
		imageAssets,
	}
}
